import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useQuery, useMutation } from "react-query";
// primereact
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { MultiSelect } from "primereact/multiselect";
import { confirmDialog } from "primereact/confirmdialog";
// components
import Modal from "../../../components/modal/Modal";
// services
import { getEnterprise } from "../../../services/GlobalRoles/getEnterprise";
import { patchEnterpriseWhatsApp } from "../../../services/Enterprise/patchEnterpriseWhatsApp";
// utils
import { queryClient } from "../../../utils/queryClient";

const ModalLinkEnterprises = ({ visible, onHide, connectionId, connectionName }) => {
  const [selectedEnterprises, setSelectedEnterprises] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  // Buscar todos os empreendimentos
  const { data: enterprises, isLoading } = useQuery(
    "enterprisesForLink",
    getEnterprise,
    {
      enabled: visible
    }
  );

  // Mutation para vincular empreendimentos
  const linkEnterpriseMutation = useMutation({
    mutationFn: patchEnterpriseWhatsApp,
    onError: (error) => {
      console.error("Erro ao vincular empreendimento:", error);
    },
  });

  // Preparar opções para o MultiSelect
  const enterpriseOptions = enterprises?.map(enterprise => ({
    id: enterprise.id,
    label: enterprise.title,
    value: enterprise.id
  })) || [];

  const handleSaveLinks = async () => {
    if (!selectedEnterprises.length) {
      toast.warning("Selecione pelo menos um empreendimento para vincular.");
      return;
    }

    setLoadingButton(true);
    const notif = toast.loading("Vinculando empreendimentos à conexão WhatsApp...");
    
    try {
      // Vincular cada empreendimento selecionado
      const promises = selectedEnterprises.map(enterpriseId => 
        linkEnterpriseMutation.mutateAsync({
          enterprise_id: enterpriseId,
          crm_whatsapp_connection_id: connectionId
        })
      );

      await Promise.all(promises);

      // Atualizar cache dos empreendimentos
      queryClient.invalidateQueries("enterprise");
      queryClient.invalidateQueries("whatsappConnections");

      setHasSaved(true);
      toast.update(notif, {
        render: `${selectedEnterprises.length} empreendimento(s) vinculado(s) com sucesso!`,
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      setTimeout(() => {
        onHide();
      }, 1000);

    } catch (error) {
      console.error("Erro ao vincular empreendimentos:", error);
      toast.update(notif, {
        render: "Erro ao vincular empreendimentos. Tente novamente.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setLoadingButton(false);
    }
  };

  const handleClose = () => {
    if (selectedEnterprises.length > 0 && !hasSaved) {
      confirmDialog({
        message: "Você selecionou empreendimentos mas ainda não salvou. Deseja cancelar a operação?",
        header: "Cancelar Vinculação",
        icon: "fa-solid fa-triangle-exclamation",
        acceptLabel: "Sim, cancelar",
        rejectLabel: "Não",
        accept: () => {
          setSelectedEnterprises([]);
          onHide();
        },
      });
    } else {
      setSelectedEnterprises([]);
      onHide();
    }
  };

  // Reset state quando modal abre
  useEffect(() => {
    if (visible) {
      setSelectedEnterprises([]);
      setHasSaved(false);
      setLoadingButton(false);
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      onHide={handleClose}
      dynamicTitle="Vincular Empreendimentos"
      closable
      style={{ width: "600px" }}
    >
      <div className="text-center px-4 py-3">
        {/* Header com gradiente WhatsApp */}
        <div 
          className="mb-4 p-4 rounded-4"
          style={{
            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
            color: "white",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <div 
            className="icon-container mx-auto mb-3"
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              border: "2px solid rgba(255,255,255,0.3)"
            }}
          >
            <i 
              className="fa-solid fa-link" 
              style={{ fontSize: "2.5rem", color: "white" }}
            />
          </div>
          <h3 className="mb-2 fw-bold">Vincular Empreendimentos</h3>
          <p className="mb-0 opacity-90">
            Conecte empreendimentos à <strong>{connectionName}</strong>
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="my-4">
            <div className="d-flex justify-content-center mb-3">
              <div 
                className="spinner-border text-primary" 
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              >
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
            <p className="text-muted">Carregando empreendimentos...</p>
          </div>
        )}

        {/* Seleção de Empreendimentos */}
        {!isLoading && enterpriseOptions.length > 0 && (
          <div className="my-4">
            <div 
              className="alert alert-info border-0 rounded-4 mb-4"
              style={{
                background: "linear-gradient(135deg, #17a2b8 0%, #20c997 100%)",
                color: "white"
              }}
            >
              <i className="fa-solid fa-info-circle me-2"></i>
              Selecione os empreendimentos que deseja vincular a esta conexão WhatsApp
            </div>

            {/* Seleção de empreendimentos */}
            <div className="mb-4">
              <label className="form-label fw-bold text-start d-block mb-3">
                <i className="fa-solid fa-building me-2 text-primary"></i>
                Selecione os empreendimentos:
              </label>
              
              <div className="position-relative">
                <MultiSelect
                  value={selectedEnterprises}
                  options={enterpriseOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Escolha os empreendimentos..."
                  filter
                  className="w-100"
                  style={{
                    borderRadius: "12px",
                    border: "2px solid #e9ecef",
                    minHeight: "50px"
                  }}
                  onChange={(e) => setSelectedEnterprises(e.value)}
                />
              </div>
              
              {selectedEnterprises.length > 0 && (
                <div className="mt-3">
                  <small className="text-success">
                    <i className="fa-solid fa-check me-1"></i>
                    {selectedEnterprises.length} empreendimento(s) selecionado(s)
                  </small>
                </div>
              )}
            </div>

            {/* Informações adicionais */}
            <div className="card border-0 rounded-4 mb-4">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="fa fa-info-circle text-primary me-2"></i>
                  O que acontece ao vincular?
                </h6>
                <ul className="list-unstyled small mb-0">
                  <li className="mb-2">
                    <i className="fa fa-check-circle text-success me-1"></i>
                    Os empreendimentos poderão usar esta conexão WhatsApp
                  </li>
                  <li className="mb-2">
                    <i className="fa fa-check-circle text-success me-1"></i>
                    Leads destes empreendimentos receberão mensagens via WhatsApp
                  </li>
                  <li className="mb-0">
                    <i className="fa fa-check-circle text-success me-1"></i>
                    A configuração será salva automaticamente
                  </li>
                </ul>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="d-flex gap-3 justify-content-center">
              <Button
                label="Cancelar"
                icon="fa-solid fa-times"
                className="btn-lg px-4 py-2"
                severity="danger"
                outlined
                style={{
                  borderRadius: "12px",
                  borderWidth: "2px",
                  transition: "all 0.3s ease"
                }}
                onClick={handleClose}
                disabled={loadingButton}
              />

              <Button
                label={loadingButton ? "Vinculando..." : "Vincular Empreendimentos"}
                icon={loadingButton ? "pi pi-spin pi-spinner" : "fa-solid fa-link"}
                className="btn-lg px-4 py-2 fw-bold text-white"
                style={{
                  background: selectedEnterprises.length > 0 
                    ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                    : "#6c757d",
                  border: "none",
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  boxShadow: selectedEnterprises.length > 0 
                    ? "0 4px 15px rgba(40, 167, 69, 0.3)"
                    : "none"
                }}
                onClick={handleSaveLinks}
                disabled={!selectedEnterprises.length || loadingButton}
              />
              </div>
            </div>
          )}
        {!isLoading && enterpriseOptions.length === 0 && (
          <div className="my-4">
            <div className="alert alert-warning border-0 rounded-4 mb-3">
              <i className="fa-solid fa-exclamation-triangle me-2"></i>
              <strong>Nenhum empreendimento encontrado</strong>
              <p className="mb-0 mt-2">
                Não existem empreendimentos disponíveis para vincular no momento.
              </p>
            </div>
            <Button
              label="Fechar"
              icon="fa-solid fa-times"
              onClick={handleClose}
              severity="secondary"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalLinkEnterprises;
